import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import axios from 'axios';
import { AppState } from 'lib/redux';
import { baseRESTUrl, teamRESTUrl } from 'lib/utils';
import { uniqBy } from 'lodash';

export interface TeamState {
  invites: any[];
  team: any;
  users: any;
  pendingDomainLevelInvites: string[];
}

export enum TeamPermissions {
  ADMIN = 'admin',
  MEMBER = 'member',
}

export enum TeamInviteType {
  INDIVIDUAL = 'individual',
  GROUP = 'group'
}

const initialState: TeamState = {
  invites: [],
  team: undefined,
  users: undefined,
  pendingDomainLevelInvites: [],
};

export const updateTeamDomain = createAsyncThunk(
  'team/updateDomain',
  async ({ team, originalIdentifier, setSubmitting }: any, { rejectWithValue }) => {
    try {
      const requestUrl = teamRESTUrl(originalIdentifier);
      const res = await axios.put(`${requestUrl}/v1/team`, team, { withCredentials: true })
      return res.data;
    } catch (err) {
      if (err?.response?.status === 400) {
        return rejectWithValue('This team subdomain has already been taken, please choose a different one.');
      }

      return rejectWithValue(err.response.statusText);
    } finally {
      setSubmitting(false);
    }
  }
)

export const updateTeam = createAsyncThunk(
  'team/update',
  async (updatedTeam: any, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`${baseRESTUrl()}/v1/team`, updatedTeam, { withCredentials: true })
      dispatch(setTeam(data));
      return data;
    } catch (err) {
      return rejectWithValue(err.response.statusText);
    }
  }
);

export const getTeam = createAsyncThunk(
  'team/getTeam',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${baseRESTUrl()}/v1/team`, { withCredentials: true });
      dispatch(setTeam(res.data));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response.statusText);
    }
  }
)

export const getTeamMembers = createAsyncThunk(
  'team/getTeamMembers',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${baseRESTUrl()}/v1/team-users`, { withCredentials: true });
      dispatch(setTeamUsers(res.data));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response.statusText);
    }
  }
);

export const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    team: (state, { payload }) => {
      const updated = { ...state.team, ...payload };
      return void(state.team = updated);
    },
    addInvites: (state, { payload }) => void(state.invites = uniqBy([...state.invites, ...payload], 'match')),
    removeInvite: (state, { payload }) => {
      const updated = state.invites.filter((invite) => invite.id !== payload);
      return void(state.invites = updated);
    },
    updateInvite: (state, { payload }) => {
      const updated = state.invites.filter((invite) => invite.match !== payload.match);
      return void(state.invites = [ ...updated, payload ]);
    },
    resetPendingDomainInvites: (state) => void(state.pendingDomainLevelInvites = []),
    addPendingDomainLevelInvite: (state, { payload }: { payload: string }) => void(state.pendingDomainLevelInvites = [...state.pendingDomainLevelInvites, payload]),
    removePendingDomainLevelInvite: (state, { payload }: { payload: string }) => {
      const filtered = state.pendingDomainLevelInvites.filter((invite) => invite !== payload);
      return void(state.pendingDomainLevelInvites = filtered);
    },
    invites: (state, { payload }) => void(state.invites = payload),
    users: (state, { payload }) => void(state.users = payload),
    removeTeamUser: (state, { payload }) => {
      const updated = state.users.filter(({user: { id }}) => id !== payload);
      return void(state.users = updated);
    },
    updateTeamUser: (state, { payload }) => {
      const users = state.users;

      const idx = users.findIndex(({user: {id}}) => id === payload.user.id);

      if (idx === -1) {
        return;
      }

      users[idx] = payload;

      return void(state.users = users);
    }
  }
})

export const {
  team: setTeam,
  users: setTeamUsers,
  invites: setTeamInvites,
  addInvites,
  removeInvite,
  addPendingDomainLevelInvite,
  removePendingDomainLevelInvite,
  removeTeamUser,
  updateTeamUser,
  updateInvite,
  resetPendingDomainInvites,
} = teamSlice.actions;

export const selectTeamInvites = (state: AppState) => state.team.invites;
export const selectTeam = (state: AppState) => state.team.team;
export const selectTeamUsers = (state: AppState) => state.team.users;
export const selectPendingDomainLevelInvites = (state: AppState) => state.team.pendingDomainLevelInvites;

export const selectInvitesByType = createSelector(
  [
    (state: any) => state,
    (_: any, type: TeamInviteType.GROUP | TeamInviteType.INDIVIDUAL) => type
  ],
  (state, type) => {
    let invites = JSON.parse(JSON.stringify(state.team.invites));
    const filteredInvites = invites.filter((invite) => invite.type === type);

    return filteredInvites.sort((a, b) => {
      if (a.match < b.match) { return -1; }
      if (a.match > b.match) { return 1; }
      return 0;
    });
  }
);

